import { motion } from "framer-motion";
import { ArrowRight, Boxes, FileText, Package, Ruler } from "lucide-react";
import Reveal from "../Reveal";
import SectionHead from "../SectionHead";

const IMG =
    "https://images.unsplash.com/photo-1542621334-a254cf47733d?q=80&w=1400&auto=format&fit=crop";

const INPUTS = ["2D Drawings", "3D Models", "Samples", "Customer Specifications"];

const SUPPORT = [
    "Drawing review",
    "Manufacturing feasibility assessment",
    "Material selection assistance",
    "Tooling discussion",
    "Process planning",
    "Prototype development",
    "Production optimization",
];

const REQUIREMENTS = ["Drawing", "Material Grade", "Quantity", "Application", "Required Tests"];

export const Engineering = () => (
    <section id="engineering" className="py-24 md:py-32 scroll-mt-20">
        <div className="shell">
            <SectionHead
                num="07"
                overline="Engineering & Customization"
                title="Send a drawing. Get a component."
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                <div className="lg:col-span-6">
                    <Reveal>
                        <div className="flex flex-wrap gap-2.5 mb-8" data-testid="engineering-input-chips">
                            {INPUTS.map((chip) => (
                                <span
                                    key={chip}
                                    className="font-display uppercase font-medium tracking-wide text-base md:text-lg border border-zinc-700 hover:border-safety hover:text-safety text-zinc-200 px-5 py-2.5 transition-colors cursor-default"
                                >
                                    {chip}
                                </span>
                            ))}
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <h3 className="font-display uppercase font-semibold text-xl text-zinc-50 mb-5 flex items-center gap-3">
                            <Boxes size={20} className="text-safety" /> Engineering Support
                        </h3>
                        <div className="border-t border-zinc-800">
                            {SUPPORT.map((s, i) => (
                                <motion.div
                                    key={s}
                                    initial={{ opacity: 0, x: -16 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.4, delay: i * 0.05 }}
                                    data-testid={`engineering-support-${i + 1}`}
                                    className="flex items-center justify-between py-3.5 border-b border-zinc-900 group"
                                >
                                    <span className="text-sm md:text-base text-zinc-300 group-hover:text-zinc-50">
                                        {s}
                                    </span>
                                    <ArrowRight
                                        size={15}
                                        className="text-zinc-700 group-hover:text-safety group-hover:translate-x-1 transition-all"
                                    />
                                </motion.div>
                            ))}
                        </div>
                    </Reveal>
                </div>

                <div className="lg:col-span-6 space-y-6">
                    <Reveal delay={0.1}>
                        <div className="relative border border-zinc-800 group overflow-hidden h-[280px] lg:h-[320px]">
                            <img
                                src={IMG}
                                alt="Engineering drawings review"
                                className="absolute inset-0 w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-transparent" />
                            <div className="absolute bottom-0 inset-x-0 p-5 font-mono text-[10px] uppercase tracking-[0.25em] text-safety">
                                Drawing Review — Feasibility — Proposal
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.2}>
                        <div
                            className="border border-safety/60 bg-gradient-to-br from-safety/10 to-transparent p-6 md:p-8"
                            data-testid="engineering-requirements-callout"
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <Ruler size={18} className="text-safety" />
                                <span className="font-display uppercase font-semibold tracking-wide text-lg text-zinc-50">
                                    For new FRP Epoxy Box requirements
                                </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mb-5">
                                {REQUIREMENTS.map((r, i) => (
                                    <span key={r} className="flex items-center gap-2">
                                        <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.1em] text-zinc-100 border border-safety/50 px-3 py-1.5 bg-[#0e0e11]">
                                            {r === "Drawing" && <FileText size={12} className="text-safety" />}
                                            {r === "Quantity" && <Package size={12} className="text-safety" />}
                                            {r}
                                        </span>
                                        {i < REQUIREMENTS.length - 1 && (
                                            <span className="text-safety font-mono">+</span>
                                        )}
                                    </span>
                                ))}
                            </div>
                            <p className="text-zinc-300 text-sm leading-relaxed">
                                Our technical team can then evaluate
                                manufacturability and provide the appropriate
                                proposal.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </div>
    </section>
);

export default Engineering;